import {abbreviateNumber} from 'common/util';
import {TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {pluralize} from 'common/util/string';

import {Bucket, BucketType} from '../types';

const abbreviate = (num) => abbreviateNumber(num, {precision: 2, trimZeroes: true});

export function getBucketType(bucket: Bucket): BucketType {
  return bucket.disabled ? BucketType.None : bucket.groups ? BucketType.Varied : BucketType.Even;
}

export function getUnevenBucketPreview(groups: number[], unit: string = null) {
  const unitSuffix = unit ? ` ${pluralize(TimeUnitLabels[unit], true)}` : ``;
  const inner = [...groups]
    .splice(1)
    .map((_, idx) => `${abbreviate(groups[idx])}-${abbreviate(groups[idx + 1])}${unitSuffix}, `)
    .join(``);

  // Should look like "<10, 10-20, 20-30, >=30"
  return groups.length
    ? `<${abbreviate(groups[0])}${unitSuffix}, ${inner}>=${abbreviate(groups[groups.length - 1])}${unitSuffix}`
    : `N/A`;
}

export function getEvenBucketPreview(min: number, max: number, binSize: number, unit: string = null) {
  // the real max is actually going to be greater if the bin doesnt go in evenly
  // for example min and max of 0 and 6 with a bucket size of 5 will have 0-5, 5-10, 10+ buckets
  // TODO change this if the backend logic changes
  const binOverflow = (max - min) % binSize;
  const realMax = binOverflow ? max - binOverflow + binSize : max;
  const unitSuffix = unit ? ` ${pluralize(TimeUnitLabels[unit], true)}` : ``;
  const previewList = [`<${abbreviate(min)}${unitSuffix}`];
  let entryCount = 0;
  const pushBucket = () =>
    previewList.push(
      `${abbreviate(min + binSize * entryCount)}-${abbreviate(min + binSize * ++entryCount)}${unitSuffix}`,
    );
  // add inner buckets depending on if the buckets exist, with an extra bucket when dealing with values with less than 3 digits
  // example output: "<0, 0-30, 30-60, 60-90, ..., ≥120"
  const range = realMax - min;
  range !== 0 && pushBucket();
  range >= binSize * 2 && pushBucket();
  range >= binSize * 3 && min + binSize * 3 < 100 && pushBucket();
  min + binSize * entryCount < realMax && previewList.push(`...`);
  previewList.push(`≥${abbreviate(realMax)}${unitSuffix}`);
  return previewList.join(`, `);
}

export function getBucketPreview(bucket: Bucket) {
  switch (getBucketType(bucket)) {
    case BucketType.None:
      return `1, 2, 3, 4, 5, ..., 100, ...`;
    case BucketType.Varied:
      return getUnevenBucketPreview(bucket.groups, bucket.unit);
    case BucketType.Even:
      return getEvenBucketPreview(bucket.min, bucket.max, bucket.bucketSize, bucket.unit);
  }
}
